import { api } from "@/lib/api";

// Unsent entry drafts, encrypted with AES-GCM in IndexedDB ("pensive" db, "drafts" store).
// The key comes from the server for the signed-in person and lives only in memory.
const DB = "pensive";
const STORE = "drafts";
let key = null;
let keyUser = null;

const openDb = () => new Promise((resolve, reject) => {
  const req = indexedDB.open(DB, 1);
  req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: "id" });
  req.onsuccess = () => resolve(req.result);
  req.onerror = () => reject(req.error);
});

const tx = async (mode, fn) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const out = fn(t.objectStore(STORE));
    t.oncomplete = () => { db.close(); resolve(out?.result); };
    t.onerror = () => { db.close(); reject(t.error); };
  });
};

const b64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

// writes and deletes run one after another so a late write never revives a confirmed draft
let chain = Promise.resolve();
const serial = (fn) => { const run = chain.then(fn, fn); chain = run.catch(() => {}); return run; };

export async function loadDraftKey(userId) {
  if (key && keyUser === userId) return true;
  try {
    const { data } = await api.get("/auth/draft-key");
    key = await crypto.subtle.importKey("raw", b64(data.key), "AES-GCM", false, ["encrypt", "decrypt"]);
    keyUser = userId;
    return true;
  } catch {
    return false;
  }
}

export const clearDraftKey = () => { key = null; keyUser = null; };
export const hasDraftKey = (userId) => !!key && keyUser === userId;

// draft: { id, saved, title, body, moods, route, writing_ms, companion }
export function putDraft(userId, draft) {
  if (!hasDraftKey(userId)) return Promise.resolve();
  const k = key;
  return serial(async () => {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const data = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, k, new TextEncoder().encode(JSON.stringify(draft)));
    await tx("readwrite", (s) => s.put({ id: draft.id, user_id: userId, iv, data, updated_at: Date.now() }));
  });
}

export const removeDraft = (id) => serial(() => tx("readwrite", (s) => s.delete(id))).catch(() => {});

const allRecords = () => tx("readonly", (s) => s.getAll()).catch(() => []);

export const countDrafts = async (userId) => (await allRecords()).filter((r) => r.user_id === userId).length;

export function clearDrafts(userId) {
  return serial(async () => {
    const mine = (await allRecords()).filter((r) => r.user_id === userId);
    await Promise.all(mine.map((r) => tx("readwrite", (s) => s.delete(r.id))));
  }).catch(() => {});
}

// sends every pending draft of this person; true when nothing is left on the device
export async function syncDrafts(userId) {
  const mine = (await allRecords()).filter((r) => r.user_id === userId);
  if (!mine.length) return true;
  if (!navigator.onLine || !(await loadDraftKey(userId))) return false;
  let left = 0;
  for (const rec of mine) {
    try {
      const plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv: rec.iv }, key, rec.data);
      const { id, saved, companion, ...payload } = JSON.parse(new TextDecoder().decode(plain));
      try {
        if (saved) await api.put(`/entries/${id}`, payload);
        else await api.post("/entries", { id, companion, ...payload });
      } catch (e) {
        if (e.response?.status !== 409) throw e;
        await api.put(`/entries/${id}`, payload);
      }
      await removeDraft(id);
    } catch {
      left += 1;
    }
  }
  return left === 0;
}
